import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {Trans} from 'react-i18next';
import {useNavigate} from 'react-router';
import {useSearchParams} from 'react-router-dom';
import {Autoplay, Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {PermissionTypeKr, RoleType} from '@di-shared/api/di/type';
import {emitEventLog} from '@di-shared/features/analytics';
import CapitalEventBannerType1 from '@di-shared/features/capital/CapitalEventBannerType1';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import {TabType} from '@di-shared/features/home/kr/HomeContents.kr';
import DrivingDistanceSectionKr from '@di-shared/features/home/kr/driving/DrivingDistanceSection.kr';
import FuelEfficiencySectionKr from '@di-shared/features/home/kr/driving/FuelEfficiencySection.kr';
import SafeDrivingSectionKr from '@di-shared/features/home/kr/driving/SafeDrivingSection.kr';
import SafeDrivingSectionErrorKr from '@di-shared/features/home/kr/driving/SafeDrivingSectionError.kr';
import SafeDrivingSectionLoadingKr from '@di-shared/features/home/kr/driving/SafeDrivingSectionLoading.kr';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import MileageConditionalRenderer from '@di-shared/features/mileage/ecomileage/MileageConditionalRenderer';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {isEventDuration} from '@di-shared/pages/[vin]/capital/event1';
import {ROUTE_PATHS} from '@di-shared/routes';
import {BrandType} from '@shared/cci/features/const/environment';

interface DrivingContentsKrProps {
  vin: string;
}

export default function DrivingContentsKr({vin}: DrivingContentsKrProps) {
  const {t} = useInferableTranslation();
  const {checkCapitalVinWhitelist} = useFirebaseStore(({checkCapitalVinWhitelist}) => ({checkCapitalVinWhitelist}));
  const [searchParams, setSearchParams] = useSearchParams();
  const {checkAvailableComponent, role} = useAvailableComponentKr({vin});
  const {openInAppBrowser} = useCcWebviewUtilsHelper();
  const brand = import.meta.env.VITE_BRAND;
  const navigate = useNavigate();

  return (
    <div className="tab_content my_trips">
      {/*// [2025-09-04:cy.seo] FIXME: 이벤트 후 삭제 필요 EVENT2*/}
      {(checkCapitalVinWhitelist(vin) || isEventDuration()) && (
        <CapitalEventBannerType1
          key="driving"
          vin={vin}
          onClick={() => {
            searchParams.set('tab', 'finance');
            setSearchParams(searchParams, {replace: true});
            emitEventLog({
              event: 'di_driveinfo_interaction',
              data_action: 'view banner',
              data_name: '똑똑한 내 차 관리 시원한 커피와 함께',
            });
          }}
        />
      )}
      <section
        className="section"
        id={`${TabType.Driving}_tabpanel`}
        aria-labelledby={`${TabType.Driving}_tab`}
      >
        {/* 안전운전점수 */}
        {checkAvailableComponent(PermissionTypeKr.SAFETY_SCORE) && (
          <ErrorBoundary fallback={<SafeDrivingSectionErrorKr vin={vin} />}>
            <Suspense fallback={<SafeDrivingSectionLoadingKr />}>
              <SafeDrivingSectionKr vin={vin} />
            </Suspense>
          </ErrorBoundary>
        )}

        {/* 서울시, 서비스 소개 배너 */}
        <div className="section section_trip_history">
          <div className="trip_history_grid">
            <div className="trip_history_grid_item">
              <Swiper
                slidesPerView={'auto'}
                className={'block-banner-swiper'}
                modules={[Pagination, Autoplay]}
                autoplay={{delay: 5000, disableOnInteraction: false}}
                pagination={{el: '.swiper-pagination', type: 'bullets'}}
                spaceBetween={18}
                observer
                observeParents
                loop
              >
                {checkAvailableComponent(PermissionTypeKr.SEOUL) && (
                  <SwiperSlide>
                    <MileageConditionalRenderer
                      vin={vin}
                      ifNotSupported={() => <></>}
                      ifSupportedNotAgree={(openMileageSingUpBottomSheet) => (
                        <SeoulMileageBannerSlide
                          onClick={() => {
                            emitEventLog({
                              event: 'di_driveinfo_interaction',
                              data_action: 'view banner',
                              data_name: t('T10187'),
                            });
                            openMileageSingUpBottomSheet();
                          }}
                        />
                      )}
                      ifAgree={(checkAndGotoMileagePage) => <SeoulMileageBannerSlide onClick={checkAndGotoMileagePage} />}
                      loadingFallback={<SeoulMileageBannerSlideLoading />}
                      errorFallback={<SeoulMileageBannerSlideError />}
                    />
                  </SwiperSlide>
                )}
                <SwiperSlide>
                  <div
                    className="driving_banner type_block"
                    style={{
                      backgroundColor:
                        import.meta.env.VITE_BRAND === BrandType.GENESIS
                          ? 'var(--primary-fixed-ultimate)'
                          : import.meta.env.VITE_BRAND === BrandType.HYUNDAI
                            ? 'var(--di-point-back)'
                            : '',
                    }}
                  >
                    <LinkTo
                      className="driving_banner_box"
                      to={ROUTE_PATHS.ABOUT.getFullPath()}
                      onClick={() => {
                        emitEventLog({
                          event: 'di_driveinfo_interaction',
                          data_action: 'view banner',
                          data_name: t('T10189'),
                        });
                      }}
                    >
                      <span className="driving_banner_txtbox">
                        <strong
                          className="driving_banner_title body_medium_b"
                          style={{whiteSpace: 'pre-wrap'}}
                        >
                          {t('T10189')}
                        </strong>
                        <span className="driving_banner_desc body_small_r">{t('T10190')}</span>
                      </span>
                      <span className="driving_banner_imgbox">
                        <ImageSelector
                          className="driving_banner_img"
                          alt="driving_banner_img"
                          kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_home_intro_block_light.png')}
                          kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_home_intro_block.png')}
                          hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_home_intro_block.png')}
                          hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_home_intro_block.png')}
                          genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_home_intro_block.png')}
                          genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_home_intro_block.png')}
                        />
                      </span>
                    </LinkTo>
                  </div>
                </SwiperSlide>
                {role !== RoleType.SECONDARY_USER && (
                  <SwiperSlide>
                    <div
                      className="driving_banner type_block"
                      style={{
                        backgroundColor:
                          import.meta.env.VITE_BRAND === BrandType.GENESIS
                            ? 'var(--primary-fixed-ultimate)'
                            : import.meta.env.VITE_BRAND === BrandType.HYUNDAI
                              ? 'var(--di-point-back)'
                              : '',
                      }}
                    >
                      <a
                        className="driving_banner_box"
                        role="button"
                        onClick={() => {
                          searchParams.set('tab', 'finance');
                          setSearchParams(searchParams, {replace: true});
                          emitEventLog({
                            event: 'di_driveinfo_interaction',
                            data_action: 'view banner',
                            data_name: '새로운 서비스가 나왔어요 자동차 보험 연결하기',
                          });
                          navigate(ROUTE_PATHS.CAPITAL_BRIDGE.getFullPath(vin));
                        }}
                      >
                        <span className="driving_banner_txtbox">
                          <strong className="driving_banner_title body_medium_b">새로운 서비스가 나왔어요</strong>
                          <span className="driving_banner_desc body_small_r">자동차 보험 연결하기</span>
                        </span>
                        <span className="driving_banner_imgbox">
                          <ImageSelector
                            className="driving_banner_img"
                            alt="driving_banner_img"
                            kiaLightSrc={() => import('@di-shared/assets/img/event/banner_home_service_light.png')}
                            kiaDarkSrc={() => import('@di-shared/assets/img/event/banner_home_service.png')}
                            hyundaiLightSrc={() => import('@di-shared/assets/img/event/hyundai/banner_home_service.png')}
                            hyundaiDarkSrc={() => import('@di-shared/assets/img/event/hyundai/banner_home_service.png')}
                            genesisLightSrc={() => import('@di-shared/assets/img/event/genesis/banner_home_service.png')}
                            genesisDarkSrc={() => import('@di-shared/assets/img/event/genesis/banner_home_service.png')}
                          />
                        </span>
                      </a>
                    </div>
                  </SwiperSlide>
                )}
                <div className="swiper-pagination" />
              </Swiper>

              {/* 평균 연비 */}
              <ErrorBoundary fallback={<FuelEfficiencySectionKr.Error />}>
                <Suspense fallback={<FuelEfficiencySectionKr.Loading />}>
                  <FuelEfficiencySectionKr vin={vin} />
                </Suspense>
              </ErrorBoundary>
            </div>

            <div className="trip_history_grid_item">
              {/* 평균 주행 */}
              <ErrorBoundary fallback={<DrivingDistanceSectionKr.Error />}>
                <Suspense fallback={<DrivingDistanceSectionKr.Loading />}>
                  <DrivingDistanceSectionKr vin={vin} />
                </Suspense>
              </ErrorBoundary>

              {/* 데이터 알아보기 배너 */}
              <div className="driving_banner type_block">
                <LinkTo
                  className="driving_banner_box"
                  to={ROUTE_PATHS.ABOUT_DATA_EXPLORE.getFullPath()}
                  onClick={() =>
                    emitEventLog({
                      event: 'di_driveinfo_interaction',
                      data_action: 'view banner',
                      data_name: t('T40140'),
                    })
                  }
                >
                  <span className="driving_banner_txtbox type_reverse">
                    <strong className="driving_banner_title body_medium_b">{t('T20095')}</strong>
                    <span className="driving_banner_desc body_small_r">{t('T20094')}</span>
                  </span>
                  <span className="driving_banner_imgbox">
                    <ImageSelector
                      className="driving_banner_img"
                      alt="driving_banner_img"
                      kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_bar_graph_block_light.png')}
                      kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_bar_graph_block.png')}
                      hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_bar_graph_block_light.png')}
                      hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_bar_graph_block.png')}
                      genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_bar_graph_block_light.png')}
                      genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_bar_graph_block.png')}
                    />
                  </span>
                </LinkTo>
              </div>
            </div>
          </div>
        </div>

        {brand === BrandType.KIA && (
          <ul className="notice_list">
            <li
              className="notice_item"
              style={{whiteSpace: 'pre-wrap'}}
            >
              <Trans
                i18nKey="T40002"
                components={{
                  Highlighted: (
                    <a
                      role="button"
                      className="notice_link"
                      onClick={(e) => {
                        e.preventDefault();
                        openInAppBrowser(import.meta.env.VITE_PRIVACY_URL);
                      }}
                    />
                  ),
                }}
              />
            </li>
          </ul>
        )}
      </section>
    </div>
  );
}

interface SeoulMileageBannerSlideProps {
  onClick(): void;
}

function SeoulMileageBannerSlide({onClick}: SeoulMileageBannerSlideProps) {
  const {t} = useInferableTranslation();
  return (
    <div
      className="driving_banner type_block"
      style={{
        backgroundColor:
          import.meta.env.VITE_BRAND === BrandType.GENESIS
            ? 'var(--primary-fixed-ultimate)'
            : import.meta.env.VITE_BRAND === BrandType.HYUNDAI
              ? 'var(--di-point-back)'
              : '',
      }}
    >
      <button
        className="driving_banner_box"
        onClick={onClick}
      >
        <span className="driving_banner_txtbox">
          <strong
            className="driving_banner_title body_medium_b"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10187')}
          </strong>
          <span className="driving_banner_desc body_small_r">{t('T10188')}</span>
        </span>
        <span className="driving_banner_imgbox">
          <ImageSelector
            className="driving_banner_img"
            alt="driving_banner_img"
            kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage_block.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage_block.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage_block.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage_block.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage_block.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage_block.png')}
          />
        </span>
      </button>
    </div>
  );
}

function SeoulMileageBannerSlideError() {
  const {t} = useInferableTranslation();
  return (
    <div className="driving_banner type_block">
      <button className="driving_banner_box">
        <span className="driving_banner_txtbox">
          <strong
            className="driving_banner_title body_medium_b"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10187')}
          </strong>
          <span className="driving_banner_desc body_small_r">{t('T10188')}</span>
        </span>
        <span className="driving_banner_imgbox">
          <ImageSelector
            className="driving_banner_img"
            alt="driving_banner_img"
            kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage_block.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_eco_mileage_block.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage_block.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_eco_mileage_block.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage_block.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_eco_mileage_block.png')}
          />
        </span>
      </button>
    </div>
  );
}

function SeoulMileageBannerSlideLoading() {
  return (
    <div
      className="driving_banner type_block skeleton"
      style={{height: 186}}
    ></div>
  );
}
